/**
 * The Smart Pen's scorers for the corner shapes in the SRS C5 library:
 * rectangle, square, triangle, right triangle, pentagon and hexagon.
 *
 * All six are the same idea — a closed stroke with a countable number of sharp
 * turns — so they share one measurement and differ only in what the corner count
 * and the angles between them mean.
 */

import type { ShapeMatch } from '@opal/contracts';
import type { Point, ShapeType } from '@opal/shared';

import {
  centroidOf,
  distance,
  findCorners,
  meanDistanceTo,
  normaliseAngle,
  scoreFromError,
  type Corner,
  type StrokePath,
} from './recognise-geometry.js';

/** Corners nearer than this share of the shape are the same corner drawn twice. */
const MIN_SIDE_RATIO = 0.06;

/**
 * A turn worth offering as a corner. Deliberately low: the fit test that follows
 * throws out the ones that were only wobble, and a real corner drawn gently is
 * far more common than a wobble sharp enough to reach this.
 */
const CANDIDATE_MIN_TURN = 0.4;

const DEGREES = 180 / Math.PI;

/** The interior angle at every corner, in degrees. */
function interiorAngles(vertices: readonly Point[]): number[] {
  const count = vertices.length;
  const angles: number[] = [];
  for (let index = 0; index < count; index += 1) {
    const here = vertices[index];
    const before = vertices[(index - 1 + count) % count];
    const after = vertices[(index + 1) % count];
    if (!here || !before || !after) {
      angles.push(0);
      continue;
    }
    const toBefore = Math.atan2(before.y - here.y, before.x - here.x);
    const toAfter = Math.atan2(after.y - here.y, after.x - here.x);
    angles.push(Math.abs(normaliseAngle(toBefore - toAfter)) * DEGREES);
  }
  return angles;
}

function sideLengths(vertices: readonly Point[]): number[] {
  const sides: number[] = [];
  for (let index = 0; index < vertices.length; index += 1) {
    const start = vertices[index];
    const end = vertices[(index + 1) % vertices.length];
    if (start && end) sides.push(distance(start, end));
  }
  return sides;
}

function mean(values: readonly number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((total, value) => total + value, 0) / values.length;
}

/** How unequal a set of numbers is, as a share of their average. */
function spreadOf(values: readonly number[]): number {
  const average = mean(values);
  if (average <= 0) return Number.POSITIVE_INFINITY;
  return (Math.max(...values) - Math.min(...values)) / average;
}

/** Positive when the corners were drawn anticlockwise on screen. */
function windingOf(vertices: readonly Point[]): number {
  let twiceArea = 0;
  for (let index = 0; index < vertices.length; index += 1) {
    const start = vertices[index];
    const end = vertices[(index + 1) % vertices.length];
    if (!start || !end) continue;
    twiceArea += start.x * end.y - end.x * start.y;
  }
  return twiceArea >= 0 ? 1 : -1;
}

/**
 * Turns the clean corners back to face the way they were drawn.
 *
 * The clean shape is built from measurements, so its first corner is arbitrary.
 * Starting it at the corner the hand started at is what makes the 150 ms morph in
 * step 5.2 slide rather than spin.
 */
function alignTo(corners: readonly Point[], drawn: readonly Point[]): Point[] {
  const forward = corners.slice();
  const backward = corners.slice().reverse();
  let best = forward;
  let bestCost = Number.POSITIVE_INFINITY;

  for (const order of [forward, backward]) {
    for (let shift = 0; shift < order.length; shift += 1) {
      const rotated = order.slice(shift).concat(order.slice(0, shift));
      let cost = 0;
      for (let index = 0; index < rotated.length; index += 1) {
        const from = rotated[index];
        const to = drawn[index];
        if (from && to) cost += distance(from, to);
      }
      if (cost < bestCost) {
        bestCost = cost;
        best = rotated;
      }
    }
  }
  return best;
}

interface Box {
  readonly corners: Point[];
  readonly width: number;
  readonly height: number;
}

/** The rectangle that best covers the drawn corners, tilted the way they are. */
function orientedBox(vertices: readonly Point[]): Box {
  let sin = 0;
  let cos = 0;
  for (let index = 0; index < vertices.length; index += 1) {
    const start = vertices[index];
    const end = vertices[(index + 1) % vertices.length];
    if (!start || !end) continue;
    // Folded into a quarter turn so that all four edges vote for the same tilt.
    const angle = Math.atan2(end.y - start.y, end.x - start.x) * 4;
    sin += Math.sin(angle);
    cos += Math.cos(angle);
  }

  const rotation = Math.atan2(sin, cos) / 4;
  const along = { x: Math.cos(rotation), y: Math.sin(rotation) };
  const across = { x: -along.y, y: along.x };
  const middle = centroidOf(vertices);

  let minU = Number.POSITIVE_INFINITY;
  let maxU = Number.NEGATIVE_INFINITY;
  let minV = Number.POSITIVE_INFINITY;
  let maxV = Number.NEGATIVE_INFINITY;
  for (const point of vertices) {
    const dx = point.x - middle.x;
    const dy = point.y - middle.y;
    const u = dx * along.x + dy * along.y;
    const v = dx * across.x + dy * across.y;
    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    minV = Math.min(minV, v);
    maxV = Math.max(maxV, v);
  }

  const corner = (u: number, v: number): Point => ({
    x: middle.x + u * along.x + v * across.x,
    y: middle.y + u * along.y + v * across.y,
  });
  return {
    corners: alignTo(
      [corner(minU, minV), corner(maxU, minV), corner(maxU, maxV), corner(minU, maxV)],
      vertices,
    ),
    width: maxU - minU,
    height: maxV - minV,
  };
}

/** The same box with its two side lengths averaged, so every side matches. */
function squareFrom(box: readonly Point[], vertices: readonly Point[]): Point[] {
  const first = box[0];
  const second = box[1];
  const third = box[2];
  if (!first || !second || !third) return box.slice();

  const width = distance(first, second);
  const height = distance(second, third);
  const side = (width + height) / 2;
  if (width <= 0 || height <= 0) return box.slice();

  const along = { x: (second.x - first.x) / width, y: (second.y - first.y) / width };
  const across = { x: (third.x - second.x) / height, y: (third.y - second.y) / height };
  const middle = centroidOf(box);
  const half = side / 2;
  const corner = (u: number, v: number): Point => ({
    x: middle.x + u * along.x + v * across.x,
    y: middle.y + u * along.y + v * across.y,
  });
  return alignTo(
    [corner(-half, -half), corner(half, -half), corner(half, half), corner(-half, half)],
    vertices,
  );
}

/** The drawn triangle with its squarest corner made exactly square. */
function withRightAngle(vertices: readonly Point[], at: number): Point[] {
  const count = vertices.length;
  const here = vertices[at];
  const before = vertices[(at - 1 + count) % count];
  const after = vertices[(at + 1) % count];
  if (!here || !before || !after) return vertices.slice();

  const backLength = distance(here, before);
  const outLength = distance(here, after);
  if (backLength <= 0 || outLength <= 0) return vertices.slice();

  const back = { x: (before.x - here.x) / backLength, y: (before.y - here.y) / backLength };
  const square = { x: -back.y, y: back.x };
  const sign =
    (after.x - here.x) * square.x + (after.y - here.y) * square.y >= 0 ? 1 : -1;
  const moved: Point = {
    x: here.x + square.x * sign * outLength,
    y: here.y + square.y * sign * outLength,
  };

  const fixed = vertices.slice();
  fixed[(at + 1) % count] = moved;
  return fixed;
}

/** A regular polygon sitting where the drawn one sat, turned the way it was turned. */
function regularFrom(vertices: readonly Point[]): Point[] {
  const middle = centroidOf(vertices);
  const first = vertices[0];
  if (!first) return vertices.slice();

  const radius = mean(vertices.map((point) => distance(point, middle)));
  const startAngle = Math.atan2(first.y - middle.y, first.x - middle.x);
  const step = ((2 * Math.PI) / vertices.length) * windingOf(vertices);

  return vertices.map((_point, index) => ({
    x: middle.x + radius * Math.cos(startAngle + step * index),
    y: middle.y + radius * Math.sin(startAngle + step * index),
  }));
}

function triangles(vertices: readonly Point[], fit: number): ShapeMatch[] {
  const angles = interiorAngles(vertices);
  const offBy = angles.map((angle) => Math.abs(angle - 90));
  const squarest = Math.min(...offBy);
  const at = offBy.indexOf(squarest);
  const matches: ShapeMatch[] = [];

  // Offered first, so that a triangle which really is square-cornered wins the tie.
  if (squarest <= 20) {
    matches.push({
      shapeType: 'rightTriangle',
      confidence: fit * scoreFromError(squarest, 8, 20),
      geometry: { points: withRightAngle(vertices, at) },
    });
  }
  matches.push({ shapeType: 'triangle', confidence: fit, geometry: { points: vertices.slice() } });
  return matches;
}

function quadrilaterals(vertices: readonly Point[], fit: number): ShapeMatch[] {
  const angles = interiorAngles(vertices);
  const squareness = scoreFromError(mean(angles.map((angle) => Math.abs(angle - 90))), 5, 22);
  if (squareness <= 0) return [];

  // Measured on the fitted box, not on the drawn corners: a corner is only ever
  // located to the nearest sample, and four of those errors make a true square
  // look lopsided enough to lose to a rectangle.
  const box = orientedBox(vertices);
  const longer = Math.max(box.width, box.height);
  const shorter = Math.min(box.width, box.height);
  const evenness = shorter > 0 ? scoreFromError(longer / shorter - 1, 0.09, 0.35) : 0;
  const matches: ShapeMatch[] = [];

  if (evenness > 0) {
    matches.push({
      shapeType: 'square',
      confidence: fit * squareness * evenness,
      geometry: { points: squareFrom(box.corners, vertices) },
    });
  }
  matches.push({
    shapeType: 'rectangle',
    confidence: fit * squareness,
    geometry: { points: box.corners },
  });
  return matches;
}

function manySided(vertices: readonly Point[], shapeType: ShapeType, fit: number): ShapeMatch[] {
  const middle = centroidOf(vertices);
  const regularity = scoreFromError(
    spreadOf(vertices.map((point) => distance(point, middle))),
    0.12,
    0.45,
  );
  const confidence = fit * regularity;
  if (confidence <= 0) return [];
  return [{ shapeType, confidence, geometry: { points: regularFrom(vertices) } }];
}

/** Scores the stroke as a shape with exactly this many corners. */
function scoreWithCorners(path: StrokePath, ranked: readonly Corner[], count: number): ShapeMatch[] {
  if (ranked.length < count) return [];

  const chosen = ranked
    .slice(0, count)
    .map((corner) => corner.index)
    .sort((a, b) => a - b);

  const vertices: Point[] = [];
  for (const index of chosen) {
    const point = path.loop[index];
    if (point) vertices.push(point);
  }
  if (vertices.length !== count) return [];
  if (Math.min(...sideLengths(vertices)) < path.diagonal * MIN_SIDE_RATIO) return [];

  const fit = scoreFromError(meanDistanceTo(path.loop, vertices, true) / path.diagonal, 0.02, 0.09);
  if (fit <= 0) return [];

  if (count === 3) return triangles(vertices, fit);
  if (count === 4) return quadrilaterals(vertices, fit);
  if (count === 5) return manySided(vertices, 'pentagon', fit);
  return manySided(vertices, 'hexagon', fit);
}

/**
 * Scores a stroke against every corner shape at once.
 *
 * It does not decide how many corners the stroke has. It tries three, four, five
 * and six, and lets each answer prove itself by how closely its clean shape lies
 * over the ink. A rough hexagon read as a pentagon leaves an obvious gap down one
 * side, and that gap is what rules the pentagon out — no angle threshold could.
 *
 * Every plausible reading comes back, most specific first, and the recogniser takes
 * the best: a square is also a rectangle, and only the numbers say which was meant.
 */
export function scorePolygons(path: StrokePath): ShapeMatch[] {
  if (!path.closed || path.diagonal <= 0) return [];

  const ranked = findCorners(path.loop, CANDIDATE_MIN_TURN, true);
  const matches: ShapeMatch[] = [];
  for (let count = 3; count <= 6; count += 1) {
    matches.push(...scoreWithCorners(path, ranked, count));
  }
  return matches;
}
